// src/components/ConsultationForm.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useUser } from '../contexts/UserContext';
import {
  getDraftConsultation,
  getSummary,
  createConsultation,
  getAnamnesisTemplates,
  submitFeedback,
} from '../api';
import { ANAMNESE_DATA } from '../anamneses';
import { useNotification } from '../contexts/NotificationContext';
import styles from './ConsultationManager.module.css';
import QuironTipTapEditor from './QuironTipTapEditor';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faThumbsUp, 
    faThumbsDown, 
    faCopy, 
    faShareNodes, 
    faArrowRotateRight,
    faCheck,
    faFileLines,
    faFilePdf
} from '@fortawesome/free-solid-svg-icons';
import { handleShareAsTxt, handleShareAsPdf, convertMarkdownToPlainText } from './ShareComponent';
import FeedbackModal from './FeedbackModal';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://quiron.app/api';

function ConsultationForm({ consultation, onSave, isVisible }) {
  const { t, i18n } = useTranslation();
  const { user } = useUser();
  const { addNotification } = useNotification();
  const [specialty, setSpecialty] = useState('');
  const [isFirstConsultation, setIsFirstConsultation] = useState(true);
  const [rawNotes, setRawNotes] = useState('');
  const [improvedNotes, setImprovedNotes] = useState('');
  const [summary, setSummary] = useState('');
  const [loadingDraft, setLoadingDraft] = useState(false);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingSave, setLoadingSave] = useState(false);
  const [userAnamnesisTemplates, setUserAnamnesisTemplates] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);
  const [templateLoadReason, setTemplateLoadReason] = useState('initial');
  
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
  const [currentFeedback, setCurrentFeedback] = useState({ type: '', content: '', contentType: '' });
  const [shareMenu, setShareMenu] = useState({ open: false, target: null });
  const [actionSuccess, setActionSuccess] = useState({ target: null, type: null });
  const shareMenuRef = useRef(null);

  const { autosaveEnabled } = useUser();

  useEffect(() => {
    const fetchUserTemplates = async () => {
      if (user && user.id) {
        setIsLoadingTemplates(true);
        try {
          const data = await getAnamnesisTemplates();
          if (Array.isArray(data)) {
            setUserAnamnesisTemplates(data);
          } else {
            setUserAnamnesisTemplates([]);
            addNotification(t('errorLoadingUserTemplates') + (data?.error ? `: ${data.error}` : ': Invalid format'), 'error');
          }
        } catch (error) {
          setUserAnamnesisTemplates([]);
        } finally {
          setIsLoadingTemplates(false);
        }
      } else {
        setUserAnamnesisTemplates([]);
      }
    };
    fetchUserTemplates();
  }, [user, addNotification, t]);

  const updateRawNotesBasedOnTemplate = useCallback(() => {
    if (specialty) {
      const consultationTypeKey = isFirstConsultation ? 'first' : 'return';
      let templateToUse = null;

      if (userAnamnesisTemplates && userAnamnesisTemplates.length > 0) {
        const foundTemplate = userAnamnesisTemplates.find(
          (template) =>
            template.specialty === specialty &&
            template.consultation_type === consultationTypeKey
        );
        if (foundTemplate) {
            templateToUse = foundTemplate.content;
        }
      }

      if (templateToUse !== null) {
        setRawNotes(templateToUse);
      } else {
        const baseText = ANAMNESE_DATA[specialty]?.[consultationTypeKey];
        setRawNotes(baseText || '');
      }
    } else {
      setRawNotes('');
    }
    setImprovedNotes('');
    setSummary('');
  }, [specialty, isFirstConsultation, userAnamnesisTemplates]);

  useEffect(() => {
    if (!isLoadingTemplates && (templateLoadReason === 'specialtyChange' || templateLoadReason === 'typeChange')) {
        updateRawNotesBasedOnTemplate();
        setTemplateLoadReason('none');
    } else if (!isLoadingTemplates && templateLoadReason === 'initial' && !rawNotes) {
        updateRawNotesBasedOnTemplate();
        setTemplateLoadReason('none');
    }
  }, [specialty, isFirstConsultation, isLoadingTemplates, userAnamnesisTemplates, templateLoadReason, rawNotes, updateRawNotesBasedOnTemplate]);

  const openFeedbackModal = (type, content, contentType) => {
    setCurrentFeedback({ type, content, contentType });
    setIsFeedbackModalOpen(true);
  };

  const handleLike = async (content, contentType) => {
    try {
      await submitFeedback({
        feedback_type: 'like',
        content_type: contentType,
        original_content: content,
        feedback_text: '',
        contact_permission: false
      });
      addNotification(t('feedbackSentSuccess'), 'success');
    } catch(err) {
      addNotification(t('feedbackSentError'), 'error');
    }
  };

  const handleFeedbackSubmit = async (comment, contactPermission) => {
    try {
      await submitFeedback({
        feedback_type: 'dislike',
        content_type: currentFeedback.contentType,
        original_content: currentFeedback.content,
        feedback_text: comment,
        contact_permission: contactPermission,
      });
      addNotification(t('feedbackSentSuccess'), 'success');
    } catch (error) {
      addNotification(t('feedbackSentError'), 'error');
    } finally {
      setIsFeedbackModalOpen(false);
    }
  };

  const handleCopy = (markdownText, target) => {
    if (!markdownText) return;
    const plainText = convertMarkdownToPlainText(markdownText);
    navigator.clipboard.writeText(plainText)
      .then(() => {
        setActionSuccess({ target, type: 'copy' });
        setTimeout(() => setActionSuccess({ target: null, type: null }), 2000);
      })
      .catch(err => {
        addNotification(t('errorCopying'), 'error');
      });
  };

  const handleShareClick = (e, target) => {
    e.stopPropagation();
    setShareMenu(prev => ({
      open: prev.target === target ? !prev.open : true,
      target: target,
    }));
  };
  
  const handleShareAction = async (shareFn, target) => {
    setShareMenu({ open: false, target: null });
    const success = await shareFn();
    if (success) {
      setActionSuccess({ target, type: 'share' });
      setTimeout(() => setActionSuccess({ target: null, type: null }), 2000);
    }
  };

  useEffect(() => {
    function handleClickOutside(event) {
      if (shareMenu.open && shareMenuRef.current && !shareMenuRef.current.contains(event.target) && !event.target.closest(`[data-share-button]`)) {
        setShareMenu({ open: false, target: null });
      }
    }
    if (shareMenu.open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [shareMenu]);

  const checkBalanceAndProceed = async (action, costEndpoint, loadingSetter, actionCost = null) => {
    try {
      loadingSetter(true);
      if (user && user.is_admin) {
        await action();
        return;
      }
      let cost = actionCost;
      if (cost === null && costEndpoint) {
        const costResponse = await fetch(`${API_BASE_URL}${costEndpoint}`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ rawNotes, improvedNotes, specialty: specialty || "" })
        });
        if (!costResponse.ok) {
            const errorData = await costResponse.json();
            throw new Error(errorData.error || `Erro ao obter custo: ${costResponse.status}`);
        }
        const costData = await costResponse.json();
        cost = costData.cost;
      }
      const balanceResponse = await fetch(`${API_BASE_URL}/dracmas/balance`, { credentials: 'include' });
      if (!balanceResponse.ok) {
        const errorData = await balanceResponse.json();
        throw new Error(errorData.error || `Erro ao obter saldo: ${balanceResponse.status}`);
      }
      const balanceData = await balanceResponse.json();
      const balance = balanceData.balance;
      if (cost === null || balance === 'infinito' || parseFloat(balance) >= parseFloat(cost)) {
        await action();
      } else {
        addNotification(t('insufficientBalance', { cost, balance }), 'error');
      }
    } catch (error) {
      addNotification(`${t('error')}: ${error.message}`, 'error');
    } finally {
      loadingSetter(false);
    }
  };

  const handleDraft = useCallback(async () => {
    if (!rawNotes.trim()) {
      addNotification(t('fillNotesToImprove'), 'warning');
      return;
    }
    setImprovedNotes('');
    setSummary('');
    await checkBalanceAndProceed(
      async () => {
        try {
          const payload = { specialty: specialty || "", is_first_consultation: isFirstConsultation, rawNotes };
          const response = await getDraftConsultation(payload);
          const draftContent = typeof response === 'string' ? response : response?.draftNotes;
          if (typeof draftContent !== 'string') {
             throw new Error(t('invalidDraftResponse'));
          }
          setImprovedNotes(draftContent);
          addNotification(t('draftSuccess'), 'success');
        } catch (error) {
          addNotification(`${t('errorGeneratingDraft')}: ${error.message}`, 'error');
        }
      },
      '/consultations/draft/cost',
      setLoadingDraft
    );
  }, [rawNotes, specialty, isFirstConsultation, addNotification, t]);

  const handleSummary = useCallback(async () => {
    if (!improvedNotes) {
      addNotification(t('generateImprovedNotesBeforeSummary'), 'warning');
      return;
    }
    setSummary('');
    await checkBalanceAndProceed(
      async () => {
        try {
          const response = await getSummary(improvedNotes);
          setSummary(response.summary);
          addNotification(t('summarySuccess'), 'success');
        } catch (error) {
          addNotification(`${t('errorGeneratingSummary')}: ${error.message}`, 'error');
        }
      },
      '/consultations/summary/cost',
      setLoadingSummary
    );
  }, [improvedNotes, addNotification, t]);

  const handleSave = async () => {
    if (!specialty || !rawNotes) {
      addNotification(t('specialtyAndRawNotesRequired'), 'warning');
      return;
    }
    if (!improvedNotes) {
      addNotification(t('improvedNotesRecommended'), 'warning');
    }
    await checkBalanceAndProceed(
      async () => {
        try {
          const payload = {
            specialty,
            is_first_consultation: isFirstConsultation,
            rawNotes,
            improvedNotes,
            summary,
          };
          await createConsultation(payload);
          addNotification(t('consultationSavedSuccess'), 'success');
          if (specialty && (isFirstConsultation !== null)) {
            const consultationTypeKey = isFirstConsultation ? 'first' : 'return';
            localStorage.removeItem(`quironAutosave_consultation_${specialty}_${consultationTypeKey}`);
            localStorage.removeItem(`quironAutosave_consultation_improved_${specialty}_${consultationTypeKey}`);
            localStorage.removeItem(`quironAutosave_consultation_summary_${specialty}_${consultationTypeKey}`);
          }
          setSpecialty('');
          setIsFirstConsultation(true);
          setRawNotes('');
          setImprovedNotes('');
          setSummary('');
          setTemplateLoadReason('initial');
        } catch (error) {
          addNotification(`${t('errorSavingConsultation')}: ${error.message}`, 'error');
        }
      },
      '/consultations/save/cost',
      setLoadingSave,
      null
    );
  };

  const handleRawNotesChange = (content) => {
    setRawNotes(content);
    setImprovedNotes('');
    setSummary('');
  };

  const handleImprovedNotesChange = (content) => {
    setImprovedNotes(content);
  };

  const handleSummaryChange = (content) => {
    setSummary(content);
  };

  const handleRawNotesRestore = useCallback(() => {
    setImprovedNotes('');
    setSummary('');
    addNotification(t('rawNotesRestoredAndOthersCleared'), 'info');
  }, [addNotification, t]);

  const editorKeyBase = `${specialty}-${isFirstConsultation ? 'first' : 'return'}`;
  const rawNotesPlaceholder = specialty ? t('anamnesisPlaceholder') : t('anamnesisPlaceholderNoSpecialty');

  return (
    <div className={styles.formSectionCard}>
      <h3 className={styles.cardTitle}>{t('newConsultation')}</h3>
      
      <label htmlFor="specialty" className={styles.formLabel}>{t('specialty')}</label>
      <select
        id="specialty"
        value={specialty}
        onChange={(e) => {
          const newSpecialty = e.target.value;
          if (newSpecialty !== specialty) {
            setSpecialty(newSpecialty);
            setTemplateLoadReason('specialtyChange');
          } else if (!newSpecialty) {
            setSpecialty('');
            setTemplateLoadReason('specialtyChange');
          }
        }}
        className={styles.formSelect}
      >
        <option value="">{t('selectSpecialtyOption')}</option>
        <option value="Cardiologia">{t('Cardiologia')}</option>
        <option value="Dermatologia">{t('Dermatologia')}</option>
        <option value="Endocrinologia e Metabologia">{t('Endocrinologia e Metabologia')}</option>
        <option value="Gastroenterologia">{t('Gastroenterologia')}</option>
        <option value="Ginecologia e Obstetrícia">{t('Ginecologia e Obstetrícia')}</option>
        <option value="Hematologia e Hemoterapia">{t('Hematologia e Hemoterapia')}</option>
        <option value="Pediatria">{t('Pediatria')}</option>
        <option value="Pneumologia">{t('Pneumologia')}</option>
      </select>

      <label className={styles.formLabel}>{t('consultationType')}</label>
      <div className={styles.formRadioGroup}>
        <label className={styles.formRadioLabel}>
          <input type="radio" name="consultationType" value="first" checked={isFirstConsultation === true} onChange={() => {
            if (isFirstConsultation !== true) {
              setIsFirstConsultation(true);
              setTemplateLoadReason('typeChange');
            }
          }} className={styles.formRadioInput}/>
          <span className={styles.formRadioCustom}></span>
          {t('firstConsultation')}
        </label>
        <label className={styles.formRadioLabel}>
          <input type="radio" name="consultationType" value="return" checked={isFirstConsultation === false} onChange={() => {
            if (isFirstConsultation !== false) {
              setIsFirstConsultation(false);
              setTemplateLoadReason('typeChange');
            }
          }} className={styles.formRadioInput}/>
          <span className={styles.formRadioCustom}></span>
          {t('returnConsultation')}
        </label>
      </div>

      <label htmlFor="rawNotes" className={styles.formLabel}>{t('anamnesisNotesOriginal')}</label>
      <QuironTipTapEditor
        key={`raw-${editorKeyBase}`}
        value={rawNotes}
        onChange={handleRawNotesChange}
        placeholder={rawNotesPlaceholder}
        height={300}
        specialty={specialty || "general"}
        consultationType={isFirstConsultation ? 'first' : 'return'}
        autosavePrefix="consultation"
        enableAutosaveRestore={autosaveEnabled}
        onRestore={handleRawNotesRestore}
      />
      
      <div className={styles.formActionsContainer}>
        <button
          onClick={handleDraft}
          className={styles.formStyledButtonPrimary}
          disabled={loadingDraft || !rawNotes.trim() || !!improvedNotes}
        >
          {loadingDraft ? t('improving') : t('improveConsultation')}
        </button>

        {improvedNotes && (
          <>
            <label htmlFor="improvedNotes" className={styles.formLabel}>{t('anamnesisImproved')}</label>
            <QuironTipTapEditor
              key={`improved-${editorKeyBase}`}
              value={improvedNotes}
              onChange={handleImprovedNotesChange}
              placeholder={t('improvedAnamnesisPlaceholder')}
              height={400}
              specialty={specialty || "general"}
              consultationType={isFirstConsultation ? 'first' : 'return'}
              autosavePrefix="consultation_improved"
              enableAutosaveRestore={false}
            />
            <div className={styles.contentActions}>
                <button onClick={() => handleLike(improvedNotes, 'improved_notes')} title={t('like')}><FontAwesomeIcon icon={faThumbsUp} /></button>
                <button onClick={() => openFeedbackModal('dislike', improvedNotes, 'improved_notes')} title={t('dislike')}><FontAwesomeIcon icon={faThumbsDown} /></button>
                <button onClick={() => handleCopy(improvedNotes, 'improved')} title={t('copy')}>
                  {actionSuccess.target === 'improved' && actionSuccess.type === 'copy' ? <FontAwesomeIcon icon={faCheck} className={styles.successIcon} /> : <FontAwesomeIcon icon={faCopy} />}
                </button>
                <button onClick={handleDraft} title={t('redoResponse')}><FontAwesomeIcon icon={faArrowRotateRight} /></button>
                <div style={{ position: 'relative' }}>
                    <button onClick={(e) => handleShareClick(e, 'improved')} title={t('share')} data-share-button>
                       {actionSuccess.target === 'improved' && actionSuccess.type === 'share' ? <FontAwesomeIcon icon={faCheck} className={styles.successIcon} /> : <FontAwesomeIcon icon={faShareNodes} />}
                    </button>
                    <div ref={shareMenuRef} className={`${styles.shareMenu} ${shareMenu.open && shareMenu.target === 'improved' ? styles.shareMenuOpen : ''}`}>
                         <button className={styles.shareMenuItem} onClick={() => handleShareAction(() => handleShareAsTxt(improvedNotes, t('improvedConsultation'), t, addNotification), 'improved')}>
                          <FontAwesomeIcon icon={faFileLines} /> {t('shareAsTxt')}
                        </button>
                         <button className={styles.shareMenuItem} onClick={() => handleShareAction(() => handleShareAsPdf(improvedNotes, addNotification, i18n), 'improved')}>
                          <FontAwesomeIcon icon={faFilePdf} /> {t('shareAsPdf')}
                        </button>
                    </div>
                </div>
            </div>
            
            <button
              onClick={handleSummary}
              className={styles.formStyledButtonPrimary}
              disabled={loadingSummary || !improvedNotes.trim() || !!summary}
            >
              {loadingSummary ? t('generatingSummary') : t('generateCaseSummary')}
            </button>
          </>
        )}

        {summary && (
          <>
            <label htmlFor="summary" className={styles.formLabel}>{t('caseSummary')}</label>
            <QuironTipTapEditor
              key={`summary-${editorKeyBase}`}
              value={summary}
              onChange={handleSummaryChange}
              placeholder={t('caseSummaryPlaceholder')}
              height={200}
              specialty={specialty || "general"}
              consultationType={isFirstConsultation ? 'first' : 'return'}
              autosavePrefix="consultation_summary"
              enableAutosaveRestore={false}
            />
            <div className={styles.contentActions}>
                <button onClick={() => handleLike(summary, 'summary')} title={t('like')}><FontAwesomeIcon icon={faThumbsUp} /></button>
                <button onClick={() => openFeedbackModal('dislike', summary, 'summary')} title={t('dislike')}><FontAwesomeIcon icon={faThumbsDown} /></button>
                <button onClick={() => handleCopy(summary, 'summary')} title={t('copy')}>
                  {actionSuccess.target === 'summary' && actionSuccess.type === 'copy' ? <FontAwesomeIcon icon={faCheck} className={styles.successIcon} /> : <FontAwesomeIcon icon={faCopy} />}
                </button>
                <button onClick={handleSummary} title={t('redoResponse')}><FontAwesomeIcon icon={faArrowRotateRight} /></button>
                 <div style={{ position: 'relative' }}>
                    <button onClick={(e) => handleShareClick(e, 'summary')} title={t('share')} data-share-button>
                       {actionSuccess.target === 'summary' && actionSuccess.type === 'share' ? <FontAwesomeIcon icon={faCheck} className={styles.successIcon} /> : <FontAwesomeIcon icon={faShareNodes} />}
                    </button>
                    <div ref={shareMenuRef} className={`${styles.shareMenu} ${shareMenu.open && shareMenu.target === 'summary' ? styles.shareMenuOpen : ''}`}>
                        <button className={styles.shareMenuItem} onClick={() => handleShareAction(() => handleShareAsTxt(summary, t('caseSummary'), t, addNotification), 'summary')}>
                           <FontAwesomeIcon icon={faFileLines} /> {t('shareAsTxt')}
                         </button>
                        <button className={styles.shareMenuItem} onClick={() => handleShareAction(() => handleShareAsPdf(summary, addNotification, i18n), 'summary')}>
                           <FontAwesomeIcon icon={faFilePdf} /> {t('shareAsPdf')}
                         </button>
                    </div>
                </div>
            </div>
          </>
        )}

        <button
          onClick={handleSave}
          className={styles.formStyledButtonSave}
          disabled={loadingSave || !rawNotes.trim() || !specialty}
        >
          {loadingSave ? t('saving') : t('saveConsultation')}
        </button>
      </div>
      <FeedbackModal
          isOpen={isFeedbackModalOpen}
          onClose={() => setIsFeedbackModalOpen(false)}
          onSubmit={handleFeedbackSubmit}
      />
    </div>
  );
}

export default ConsultationForm;